import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
  FaPrint,
  FaTable,
} from "react-icons/fa";

import { fetchSyncRecords } from "./fieldAppApi";
import FieldAppHarvestLossSpreadsheet from "./FieldAppHarvestLossSpreadsheet";
import FieldAppTelemetryMapPanel from "./FieldAppTelemetryMapPanel";
import {
  buildHarvestLossSampleMapView,
  formatHarvestLossRate,
  harvestLossSampleFromSyncRecord,
  parseHarvestLossPlan,
  summarizeHarvestLossSamplesByMachine,
} from "./harvestLossMetrics";
import {
  buildHarvestLossCollapsedPreviewLines,
  buildHarvestLossSpreadsheetCsvRows,
  buildHarvestLossSpreadsheetFileName,
  downloadHarvestLossSpreadsheetCsv,
} from "./harvestLossSpreadsheetExport";
import {
  exportTelemetrySchedulePdf,
  printTelemetrySchedule,
} from "./fieldAppTelemetryScheduleExport";

import "./FieldAppHarvestLossOrderResults.css";
import "./FieldAppHarvestLossSpreadsheet.css";
import "./FieldAppTelemetryMapPanel.css";
import "./FieldAppTelemetrySchedulePanel.css";

const COLLAPSED_PREVIEW_LINES = 2;

function CollapsedPreviewLine({ line, t }) {
  return (
    <div
      className={`field-app-harvest-loss-preview-line${
        line.isTotal ? " field-app-harvest-loss-preview-line--total" : ""
      }`}
    >
      <strong>{line.label}</strong>
      <span>
        {line.sampleCount} {t("amostra(s)")} ·{" "}
        {formatHarvestLossRate(line.avgKgPerHa, t("kg/ha"))} ·{" "}
        {formatHarvestLossRate(line.avgBagsPerHa, t("sc/ha"), 3)}
      </span>
    </div>
  );
}

export default function FieldAppHarvestLossOrderResults({
  farmId,
  observationId,
  serviceOrderPlan,
  serviceOrderPlanPublished,
  orderLabel,
  selection,
  defaultExpanded = false,
  t,
}) {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [mapView, setMapView] = useState(null);
  const printAreaRef = useRef(null);

  useEffect(() => {
    if (defaultExpanded) {
      setExpanded(true);
    }
  }, [defaultExpanded, observationId]);

  const harvestConfig = useMemo(
    () =>
      parseHarvestLossPlan(serviceOrderPlan) ||
      parseHarvestLossPlan(serviceOrderPlanPublished),
    [serviceOrderPlan, serviceOrderPlanPublished]
  );

  const mapSelection = useMemo(() => {
    const base = selection || {};
    if (mapView && mapView.operatorIds && mapView.operatorIds.length) {
      return {
        ...base,
        operatorIds: mapView.operatorIds,
      };
    }
    return base;
  }, [selection, mapView]);

  useEffect(() => {
    if (!farmId || !observationId) return undefined;

    let cancelled = false;

    async function loadSamples() {
      setLoading(true);
      try {
        const response = await fetchSyncRecords(
          farmId,
          { event_type: "PERDA_COLHEITA_AMOSTRA", limit: 200 },
          selection
        );
        if (cancelled) return;

        const rows = (response.results || response || [])
          .map(harvestLossSampleFromSyncRecord)
          .filter(Boolean)
          .filter(
            (sample) =>
              sample.serviceOrderId != null &&
              Number(sample.serviceOrderId) === Number(observationId)
          )
          .sort((a, b) => {
            const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return ta - tb;
          });

        setSamples(rows);
      } catch (_) {
        if (!cancelled) setSamples([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSamples();
    return () => {
      cancelled = true;
    };
  }, [farmId, observationId, selection]);

  const machineBreakdown = useMemo(
    () => summarizeHarvestLossSamplesByMachine(samples),
    [samples]
  );

  const previewLines = useMemo(
    () => buildHarvestLossCollapsedPreviewLines(machineBreakdown, t),
    [machineBreakdown, t]
  );

  const resolvedOrderLabel = orderLabel || (observationId ? `#${observationId}` : "");

  const exportFileName = useMemo(
    () =>
      buildHarvestLossSpreadsheetFileName({
        observationId,
        orderLabel: resolvedOrderLabel,
      }),
    [observationId, resolvedOrderLabel]
  );

  const handleOpenSampleMap = useCallback(
    (sample) => {
      const view = buildHarvestLossSampleMapView(sample, t);
      if (!view) return;
      setMapView(view);
    },
    [t]
  );

  const handleExportCsv = useCallback(() => {
    const rows = buildHarvestLossSpreadsheetCsvRows({
      machineBreakdown,
      samples,
      harvestConfig,
      orderLabel: resolvedOrderLabel,
      t,
    });
    downloadHarvestLossSpreadsheetCsv(rows, exportFileName);
  }, [
    machineBreakdown,
    samples,
    harvestConfig,
    resolvedOrderLabel,
    exportFileName,
    t,
  ]);

  const ensureExpandedForExport = useCallback(async () => {
    if (expanded) return;
    setExpanded(true);
    await new Promise((resolve) => window.setTimeout(resolve, 120));
  }, [expanded]);

  const handlePrint = useCallback(async () => {
    await ensureExpandedForExport();
    printTelemetrySchedule();
  }, [ensureExpandedForExport]);

  const handleExportPdf = useCallback(async () => {
    if (exportingPdf) return;
    await ensureExpandedForExport();
    if (!printAreaRef.current) return;
    setExportingPdf(true);
    try {
      await exportTelemetrySchedulePdf(
        printAreaRef.current,
        exportFileName.replace(/\.csv$/i, ".pdf")
      );
    } finally {
      setExportingPdf(false);
    }
  }, [ensureExpandedForExport, exportFileName, exportingPdf]);

  if (!harvestConfig) return null;

  const hasSamples = samples.length > 0;
  const hiddenPreviewCount = Math.max(0, previewLines.length - COLLAPSED_PREVIEW_LINES);
  const canExport = hasSamples && !loading;

  return (
    <div className="field-app-harvest-loss-order-results">
      <div className="field-app-harvest-loss-panel">
        <div className="field-app-harvest-loss-panel__header">
          <button
            type="button"
            className="field-app-harvest-loss-panel__toggle btn btn-default"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            <FaTable aria-hidden="true" />
            <span>{t("Planilha de perda de colheita")}</span>
            {hasSamples ? (
              <span className="field-app-harvest-loss-panel__count">
                {samples.length} {t("amostra(s)")}
              </span>
            ) : null}
            {expanded ? (
              <FaChevronUp aria-hidden="true" />
            ) : (
              <FaChevronDown aria-hidden="true" />
            )}
          </button>

          {canExport ? (
            <div className="field-app-harvest-loss-panel__toolbar">
              <button
                type="button"
                className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                onClick={handleExportCsv}
                aria-label={t("Exportar planilha CSV (Excel)")}
                title={t("Exportar planilha CSV (Excel)")}
              >
                <FaFileDownload aria-hidden="true" />
                <span className="field-app-harvest-loss-panel__toolbar-label">CSV</span>
              </button>
              <button
                type="button"
                className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                onClick={handlePrint}
                aria-label={t("Imprimir planilha")}
                title={t("Imprimir planilha")}
              >
                <FaPrint aria-hidden="true" />
              </button>
              <button
                type="button"
                className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                onClick={handleExportPdf}
                disabled={exportingPdf}
                aria-label={t("Salvar planilha em PDF")}
                title={
                  exportingPdf ? t("Gerando PDF…") : t("Salvar planilha em PDF")
                }
              >
                <FaFileDownload aria-hidden="true" />
                <span className="field-app-harvest-loss-panel__toolbar-label">PDF</span>
              </button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <p className="field-app-harvest-loss-loading">{t("Carregando amostras...")}</p>
        ) : null}

        {!loading && !hasSamples ? (
          <p className="field-app-harvest-loss-empty-note">
            {t(
              "Nenhuma amostra registrada ainda. O operador deve fazer pelo menos {{count}} repetições.",
              { count: harvestConfig.minRepetitions }
            )}
          </p>
        ) : null}

        {!loading && hasSamples && !expanded ? (
          <div className="field-app-harvest-loss-collapsed">
            {previewLines.slice(0, COLLAPSED_PREVIEW_LINES).map((line) => (
              <CollapsedPreviewLine key={line.key} line={line} t={t} />
            ))}
            <button
              type="button"
              className="field-app-harvest-loss-expand-btn"
              onClick={() => setExpanded(true)}
            >
              {hiddenPreviewCount > 0
                ? t("Expandir planilha (+{{count}} linhas)", {
                    count: hiddenPreviewCount,
                  })
                : t("Expandir planilha completa ({{count}} amostras)", {
                    count: samples.length,
                  })}
            </button>
          </div>
        ) : null}

        {!loading && hasSamples && expanded ? (
          <div className="field-app-harvest-loss-panel__body">
            <p className="field-app-telemetry-schedule__hint field-app-harvest-loss-panel__hint">
              {t(
                "Clique nas coordenadas GPS para abrir o mapa com rastro do operador. Use o botão de download para exportar CSV (Excel)."
              )}
            </p>
            <div
              ref={printAreaRef}
              className="field-app-harvest-loss-spreadsheet__print-area"
            >
              <FieldAppHarvestLossSpreadsheet
                machineBreakdown={machineBreakdown}
                samples={samples}
                harvestConfig={harvestConfig}
                orderLabel={resolvedOrderLabel}
                onOpenMap={handleOpenSampleMap}
                t={t}
              />
            </div>
          </div>
        ) : null}
      </div>

      {mapView ? (
        <FieldAppTelemetryMapPanel
          farmId={farmId}
          selection={mapSelection}
          view={mapView}
          onClose={() => setMapView(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}
